import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MAT_NATIVE_DATE_FORMATS } from '@angular/material/core';
import { ProjectFinancementOperations } from 'app/modules/models/projectFinancement.type';
import { DossierOperationOption, SelectedDossierOperation } from 'app/modules/models/dossierOperation.type';
import { DossierDocumentType, OperationDocumentsState } from 'app/modules/models/dossierDocument.type';
import { CustomDateAdapter } from 'app/shared/adapter/customDateAdapter';
import { OperationsService } from 'app/shared/services/operations.service';
import { getActorGuid } from 'app/shared/utils/actor-identifiers.util';
import { Subscription } from 'rxjs';
import { operationOptions, resolveOperationFile, selectOperation } from './dossier-operation-selection';
import { DossierOperationDocumentsService } from './dossier-operation-documents.service';

@Component({
  selector: 'app-dossier-operations',
  templateUrl: './dossier-operations.component.html',
  host: { class: 'block' },
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        ...MAT_NATIVE_DATE_FORMATS,
        display: { ...MAT_NATIVE_DATE_FORMATS.display, dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' } },
      },
    },
  ],
})
export class DossierOperationsComponent implements OnChanges, OnDestroy {
  @Input() partner: any;
  @Input() mandataire: any;
  // Local transitions remain available for future persistence effects.
  @Output() operationAdded = new EventEmitter<SelectedDossierOperation>();
  @Output() operationRemoved = new EventEmitter<SelectedDossierOperation>();

  readonly dateControl = new FormControl(null, Validators.required);
  readonly searchControl = new FormControl({ value: '', disabled: true });
  selectedOperations: SelectedDossierOperation[] = [];
  options: DossierOperationOption[] = [];
  coupDePouce = false;
  loading = false;
  loadFailed = false;
  documentsByCeeOperationFileId: Record<string, OperationDocumentsState> = {};
  private documentSubscriptions = new Subscription();
  private catalogueSubscription?: Subscription;
  private readonly dateSubscription: Subscription;

  constructor(
    private readonly operationsService: OperationsService,
    private readonly operationDocumentsService: DossierOperationDocumentsService,
  ) {
    this.dateSubscription = this.dateControl.valueChanges.subscribe(() => this.loadOperations());
  }

  ngOnChanges(): void {
    this.loadOperations();
  }

  ngOnDestroy(): void {
    this.dateSubscription.unsubscribe();
    this.catalogueSubscription?.unsubscribe();
    this.documentSubscriptions.unsubscribe();
  }

  get hasValidDate(): boolean {
    const date = this.dateControl.value;
    return this.dateControl.valid && date instanceof Date && !isNaN(date.getTime());
  }

  get placeholder(): string {
    return this.hasValidDate ? 'Rechercher une opération...' : "Veuillez renseigner une date d'engagement";
  }

  get validationMessage(): string {
    if (!this.hasValidDate) return "Veuillez renseigner une date d'engagement valide.";
    if (this.loading) return 'Veuillez patienter pendant le chargement des opérations.';
    if (this.loadFailed) return 'Veuillez recharger les opérations avant de déposer le dossier.';
    return this.selectedOperations.length ? '' : 'Veuillez sélectionner au moins une opération.';
  }

  get valid(): boolean {
    return !this.validationMessage;
  }

  get documentTypes(): DossierDocumentType[] {
    return this.selectedOperations.flatMap(operation =>
      this.documentsByCeeOperationFileId[operation.ceeOperationFileId]?.documents || []
    );
  }

  get filteredGroups(): { sector: string; options: DossierOperationOption[] }[] {
    const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('fr');
    const search = typeof this.searchControl.value === 'string' ? normalize(this.searchControl.value.trim()) : '';
    const groups = new Map<string, DossierOperationOption[]>();
    const options = operationOptions(this.options.map(option => option.operation), this.dateControl.value, this.coupDePouce);
    options.filter(option => normalize(option.label).includes(search)).forEach(option => {
      if (!groups.has(option.sector)) groups.set(option.sector, []);
      groups.get(option.sector).push(option);
    });
    return Array.from(groups, ([sector, options]) => ({ sector, options }));
  }

  get hasCdpSelection(): boolean {
    return this.options.some(option =>
      this.selectedOperations.some(selected => selected.genericOperationId === option.operation.id) &&
      !!resolveOperationFile(option.operation, this.dateControl.value, true)
    );
  }

  readonly displayOperation = (): string => '';
  trackGroup = (_index: number, group: { sector: string }): string => group.sector;
  trackOption = (_index: number, option: DossierOperationOption): string => option.operation.id;
  trackSelection = (_index: number, operation: SelectedDossierOperation): string => operation.genericOperationId;

  onAdd(option: DossierOperationOption): void {
    this.searchControl.setValue('');
    if (this.searchControl.disabled || this.selectedOperations.some(selected => selected.genericOperationId === option.operation.id)) return;
    const file = resolveOperationFile(option.operation, this.dateControl.value, this.coupDePouce) || (!this.coupDePouce && option.file);
    if (!file) return;
    const selected = selectOperation(option.operation, file, this.coupDePouce);
    this.selectedOperations = [...this.selectedOperations, selected];
    this.loadDocumentTypes(selected.ceeOperationFileId);
    this.operationAdded.emit(selected);
  }

  onRemove(operation: SelectedDossierOperation): void {
    this.selectedOperations = this.selectedOperations.filter(selected => selected.genericOperationId !== operation.genericOperationId);
    if (!this.hasCdpSelection) this.coupDePouce = false;
    this.operationRemoved.emit(operation);
  }

  onCoupDePouceChange(cdp: boolean): void {
    this.coupDePouce = cdp;
    const previousSelections = this.selectedOperations;
    this.selectedOperations = this.selectedOperations.map(selected => {
      const option = this.options.find(item => item.operation.id === selected.genericOperationId);
      const file = resolveOperationFile(option.operation, this.dateControl.value, cdp) || option.file;
      return selectOperation(option.operation, file, cdp);
    });
    this.selectedOperations.forEach((selected, index) => {
      if (selected.ceeOperationFileId !== previousSelections[index].ceeOperationFileId) {
        this.loadDocumentTypes(selected.ceeOperationFileId);
      }
    });
  }

  validate(): boolean {
    this.dateControl.markAsTouched();
    return this.valid;
  }

  reset(): void {
    this.dateControl.reset();
  }

  getMappedOperations(address: { adresse?: string; complement?: string; cp?: string; ville?: string }): ProjectFinancementOperations[] {
    return this.selectedOperations.map((operation, index) => ({
      ...operation,
      adresse: address.adresse || '',
      complement: address.complement || '',
      cp: address.cp || '',
      ville: address.ville || '',
      siteCopro: null,
      rank: index + 1,
      ceeVolume: 0,
      classicVolume: 0,
      modesteVolume: 0,
      precaVolume: 0,
      sousTraitantId: null,
      baliseIn: [],
      baliseInToSave: [],
      fieldsUsed: [],
      caseUsedTera: null,
      marqueName: null,
      referenceName: null,
    }));
  }

  private loadDocumentTypes(ceeOperationFileId: string): void {
    const state: OperationDocumentsState = { documents: [], loading: true, failed: false };
    this.documentsByCeeOperationFileId[ceeOperationFileId] = state;
    this.documentSubscriptions.add(this.operationDocumentsService.getAllByCeeOperationFile(ceeOperationFileId).subscribe({
      next: documents => {
        state.documents = documents;
        state.loading = false;
      },
      error: () => {
        state.loading = false;
        state.failed = true;
      },
    }));
  }

  private loadOperations(): void {
    this.documentSubscriptions.unsubscribe();
    this.documentSubscriptions = new Subscription();
    this.documentsByCeeOperationFileId = {};
    this.catalogueSubscription?.unsubscribe();
    this.options = [];
    this.selectedOperations = [];
    this.coupDePouce = false;
    this.searchControl.reset('', { emitEvent: false });
    this.searchControl.disable({ emitEvent: false });
    this.loading = false;
    this.loadFailed = false;

    const partnerId = getActorGuid(this.partner);
    if (!partnerId || !this.hasValidDate) return;
    this.loading = true;
    const date = this.dateControl.value as Date;
    this.catalogueSubscription = this.operationsService.getAuthorizedByContract(
      partnerId, getActorGuid(this.mandataire) || '', date.toISOString()
    ).subscribe({
      next: operations => {
        this.options = operationOptions(operations || [], date);
        this.loading = false;
        this.searchControl.enable({ emitEvent: false });
      },
      error: () => {
        this.loading = false;
        this.loadFailed = true;
      },
    });
  }
}
